/**
 * Safe LocalStorage Utilities
 * Protects against malformed JSON, corrupted data, or quota errors.
 */

export function safeLocalStorageGet<T>(
  key: string,
  defaultValue: T,
  validator?: (value: any) => boolean
): T {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null || raw === undefined) {
      return defaultValue
    }

    // If defaultValue is string and raw is not JSON encoded object, return raw
    if (typeof defaultValue === 'string') {
      if (validator && !validator(raw)) return defaultValue
      return raw as unknown as T
    }

    const parsed = JSON.parse(raw)
    if (validator && !validator(parsed)) {
      return defaultValue
    }
    return parsed as T
  } catch (err) {
    console.warn(`[safeLocalStorageGet] Failed to parse key "${key}", falling back to default:`, err)
    return defaultValue
  }
}

export function safeLocalStorageSet(key: string, value: any): boolean {
  try {
    const str = typeof value === 'string' ? value : JSON.stringify(value)
    localStorage.setItem(key, str)
    return true
  } catch (err) {
    console.error(`[safeLocalStorageSet] Failed to save key "${key}":`, err)
    return false
  }
}

export function safeLocalStorageRemove(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // Ignore storage errors
  }
}
